import type { AnalyticsFilter, Granularity } from "@seed-factory/core";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { type PeriodPreset, rangeForPreset } from "../lib/dates";

type FiltersContextValue = {
  filter: AnalyticsFilter;
  preset: PeriodPreset;
  setPreset: (preset: PeriodPreset) => void;
  setGranularity: (value: Granularity) => void;
  setSeedId: (value?: string) => void;
  setDeploymentId: (value?: string) => void;
  setPlatformId: (value?: string) => void;
  setCustomRange: (from: Date, to: Date) => void;
};
const FiltersContext = createContext<FiltersContextValue | null>(null);

export function FiltersProvider({ children }: PropsWithChildren) {
  const initial = rangeForPreset("30d");
  const [preset, setPresetState] = useState<PeriodPreset>("30d");
  const [filter, setFilter] = useState<AnalyticsFilter>({ ...initial, granularity: "day" });
  const setPreset = useCallback((next: PeriodPreset) => {
    setPresetState(next);
    if (next !== "custom") setFilter((current) => ({ ...current, ...rangeForPreset(next) }));
  }, []);
  const value = useMemo<FiltersContextValue>(
    () => ({
      filter,
      preset,
      setPreset,
      setGranularity: (granularity) => setFilter((current) => ({ ...current, granularity })),
      setSeedId: (seedId) =>
        setFilter((current) => ({ ...current, seedId, deploymentId: undefined })),
      setDeploymentId: (deploymentId) => setFilter((current) => ({ ...current, deploymentId })),
      setPlatformId: (platformId) =>
        setFilter((current) => ({ ...current, platformId, deploymentId: undefined })),
      setCustomRange: (from, to) => {
        setPresetState("custom");
        setFilter((current) => ({ ...current, from, to }));
      },
    }),
    [filter, preset, setPreset],
  );
  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useFilters() {
  const value = useContext(FiltersContext);
  if (!value) throw new Error("useFilters must be used inside FiltersProvider");
  return value;
}
